import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Subscription data configuration
const SUBSCRIPTION_DATA = {
  value: 90,
  currency: "USD",
  content_ids: ["hubflo-subscription"],
  contents: [{ id: "hubflo-subscription", quantity: 1 }],
  content_type: "product",
  content_name: "Hubflo AI Subscription",
  num_items: 1,
} as const;

// Global type declarations for analytics
declare global {
  interface Window {
    analytics: {
      page: (properties?: Record<string, any>) => void;
      track: (event: string, properties?: Record<string, any>) => void;
      identify: (userId: string, traits?: Record<string, any>) => void;
      group: (groupId: string, traits?: Record<string, any>) => void;
      reset: () => void;
    };
    fbq: (
      action: string,
      event?: string,
      parameters?: Record<string, any>,
    ) => void;
    dataLayer: any[];
  }
}

/**
 * Ensure GTM dataLayer exists
 */
const ensureDataLayer = () => {
  if (typeof window !== "undefined" && !window.dataLayer) {
    window.dataLayer = [];
  }
};

export const analytics = {
  /**
   * Identify a user in Segment with comprehensive profile data
   */
  identify: (
    userId: string,
    traits?: {
      email?: string;
      name?: string;
      firstName?: string;
      lastName?: string;
      organizationId?: string;
      organizationName?: string;
      plan?: string;
      industry?: string;
      [key: string]: any;
    },
  ) => {
    if (window.analytics) {
      window.analytics.identify(userId, traits);
    }
  },

  /**
   * Group user with their organization in Segment
   */
  group: (
    organizationId: string,
    traits?: {
      name?: string;
      plan?: string;
      industry?: string;
      website?: string;
      employeeCount?: number;
      subscriptionStatus?: string;
      [key: string]: any;
    },
  ) => {
    if (window.analytics) {
      window.analytics.group(organizationId, traits);
    }
  },

  /**
   * Track page views - Segment, Facebook Pixel, and GTM dataLayer
   */
  trackPageView: (properties?: {
    page?: string;
    title?: string;
    content_name?: string;
    content_category?: string;
    isInitialLoad?: boolean;
    [key: string]: any;
  }) => {
    // Segment page tracking
    if (window.analytics) {
      window.analytics.page(properties);
    }

    // Facebook Pixel PageView - only for SPA navigation (not initial load)
    // Initial PageView is handled in index.html to avoid race conditions
    if (window.fbq && !properties?.isInitialLoad) {
      const fbParams: Record<string, any> = {};
      if (properties?.content_name)
        fbParams.content_name = properties.content_name;
      if (properties?.content_category)
        fbParams.content_category = properties.content_category;

      window.fbq("track", "PageView", fbParams);
    }

    // GTM dataLayer push for SPA navigation
    if (!properties?.isInitialLoad) {
      ensureDataLayer();
      window.dataLayer.push({
        event: "spa_page_view",
        page_title: properties?.title || document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
        content_name: properties?.content_name,
        content_category: properties?.content_category,
        ...properties,
      });
    }
  },

  /**
   * Track a user sign in
   */
  trackSignIn: (userId: string, properties?: Record<string, any>) => {
    if (window.analytics) {
      window.analytics.identify(userId, properties);
      window.analytics.track("User Signed In", properties);
    }
  },

  /**
   * Track a user sign up - Facebook Pixel CompleteRegistration event
   */
  trackSignUp: (userId: string, properties?: Record<string, any>) => {
    if (window.analytics) {
      window.analytics.identify(userId, properties);
      window.analytics.track("User Signed Up", properties);
    }

    // Facebook Pixel CompleteRegistration
    if (window.fbq) {
      window.fbq("track", "CompleteRegistration", {
        content_name: "User Registration",
        content_category: "signup",
        status: true,
        ...properties,
      });
    }

    // GTM dataLayer push
    ensureDataLayer();
    window.dataLayer.push({
      event: "CompleteRegistration",
      user_id: userId,
      content_name: "User Registration",
      content_category: "signup",
      status: true,
      ...properties,
    });
  },

  /**
   * Track agent creation
   */
  trackAgentCreated: (properties: {
    agentId: string;
    agentName: string;
    [key: string]: any;
  }) => {
    if (window.analytics) {
      window.analytics.track("Agent Created", properties);
    }
  },

  /**
   * Track purchase events - Facebook Pixel Purchase
   */
  trackPurchase: (properties?: {
    value?: number;
    currency?: string;
    transactionId?: string;
    planId?: string;
    [key: string]: any;
  }) => {
    const purchaseData = {
      ...SUBSCRIPTION_DATA,
      ...properties,
    };

    if (window.analytics) {
      window.analytics.track("Purchase", purchaseData);
    }

    // Facebook Pixel Purchase
    if (window.fbq) {
      window.fbq("track", "Purchase", {
        value: purchaseData.value,
        currency: purchaseData.currency,
        content_ids: purchaseData.content_ids,
        content_type: purchaseData.content_type,
        content_name: purchaseData.content_name,
        num_items: purchaseData.num_items,
        ...properties,
      });
    }

    // GTM dataLayer push
    ensureDataLayer();
    window.dataLayer.push({
      event: "Purchase",
      value: purchaseData.value,
      currency: purchaseData.currency,
      content_ids: purchaseData.content_ids,
      content_type: purchaseData.content_type,
      content_name: purchaseData.content_name,
      num_items: purchaseData.num_items,
      ...properties,
    });
  },

  /**
   * Track checkout initiation - Facebook Pixel InitiateCheckout
   */
  trackCheckoutInitiated: (properties?: Record<string, any>) => {
    const checkoutData = {
      ...SUBSCRIPTION_DATA,
      ...properties,
    };

    if (window.analytics) {
      window.analytics.track("Checkout Initiated", checkoutData);
    }

    // Facebook Pixel InitiateCheckout
    if (window.fbq) {
      window.fbq("track", "InitiateCheckout", {
        value: checkoutData.value,
        currency: checkoutData.currency,
        content_ids: checkoutData.content_ids,
        content_type: checkoutData.content_type,
        num_items: checkoutData.num_items,
        ...properties,
      });
    }

    // GTM dataLayer push
    ensureDataLayer();
    window.dataLayer.push({
      event: "InitiateCheckout",
      value: checkoutData.value,
      currency: checkoutData.currency,
      content_ids: checkoutData.content_ids,
      content_type: checkoutData.content_type,
      num_items: checkoutData.num_items,
      ...properties,
    });
  },

  /**
   * Comprehensive user identification with organization data
   * Call this whenever user logs in or user/organization data changes
   */
  identifyUser: (
    user: {
      id: string;
      email?: string;
      user_metadata?: any;
    },
    organization?: {
      id: string;
      name: string;
      plan_id?: string;
      industry_id?: string;
      website?: string;
      subscription_status?: string;
      active_agents_count?: number;
      created_at?: string;
      [key: string]: any;
    },
    userProfile?: {
      firstName?: string;
      lastName?: string;
      [key: string]: any;
    },
  ) => {
    if (!window.analytics) return;

    // Prepare user traits
    const userTraits: Record<string, any> = {
      email: user.email,
      userId: user.id,
    };

    // Add name information if available
    if (userProfile?.firstName) {
      userTraits.firstName = userProfile.firstName;
    }
    if (userProfile?.lastName) {
      userTraits.lastName = userProfile.lastName;
    }
    if (userProfile?.firstName && userProfile?.lastName) {
      userTraits.name = `${userProfile.firstName} ${userProfile.lastName}`;
    }

    // Add organization context
    if (organization) {
      userTraits.organizationId = organization.id;
      userTraits.organizationName = organization.name;
      userTraits.plan = organization.plan_id;
      userTraits.industry = organization.industry_id;
      userTraits.subscriptionStatus = organization.subscription_status;
      userTraits.activeAgentsCount = organization.active_agents_count;
    }

    // Add user metadata
    if (user.user_metadata) {
      Object.assign(userTraits, user.user_metadata);
    }

    // Identify user in Segment
    window.analytics.identify(user.id, userTraits);

    // Group user with organization in Segment
    if (organization) {
      const orgTraits = {
        name: organization.name,
        plan: organization.plan_id,
        industry: organization.industry_id,
        website: organization.website,
        subscriptionStatus: organization.subscription_status,
        activeAgentsCount: organization.active_agents_count,
        createdAt: organization.created_at,
      };

      window.analytics.group(organization.id, orgTraits);
    }
  },

  /**
   * Track conversation started event
   */
  trackConversationStarted: (properties: {
    agentId: string;
    agentName: string;
    conversationId: string;
    userType?: "lead" | "customer" | "anonymous";
    [key: string]: any;
  }) => {
    if (window.analytics) {
      window.analytics.track("Conversation Started", properties);
    }
  },

  /**
   * Track knowledge addition events
   */
  trackKnowledgeAdded: (properties: {
    agentId: string;
    knowledgeType: "document" | "website" | "text";
    knowledgeSize?: number;
    [key: string]: any;
  }) => {
    if (window.analytics) {
      window.analytics.track("Knowledge Added", properties);
    }
  },

  /**
   * Track subscription events
   */
  trackSubscriptionEvent: (
    event: "created" | "updated" | "cancelled" | "renewed",
    properties?: {
      planId?: string;
      planName?: string;
      value?: number;
      currency?: string;
      [key: string]: any;
    },
  ) => {
    if (window.analytics) {
      window.analytics.track(
        `Subscription ${event.charAt(0).toUpperCase() + event.slice(1)}`,
        properties,
      );
    }
  },

  /**
   * Track page visits for key pages
   */
  trackPageVisit: (pageName: string, properties?: Record<string, any>) => {
    if (window.analytics) {
      window.analytics.track("Page Visited", {
        pageName,
        ...properties,
      });
    }

    // Facebook Pixel ViewContent for important pages
    if (
      window.fbq &&
      ["dashboard", "agents", "pricing", "checkout"].includes(
        pageName.toLowerCase(),
      )
    ) {
      window.fbq("track", "ViewContent", {
        content_name: pageName,
        content_category: "page_visit",
        ...properties,
      });
    }
  },

  /**
   * Reset analytics (usually on logout)
   */
  reset: () => {
    if (window.analytics) {
      window.analytics.reset();
    }
  },
};

/**
 * Hook for tracking page views with React Router
 */
export function usePageTracking() {
  const location = useLocation();
  const prevLocationRef = useRef<string>();
  const isInitialLoadRef = useRef<boolean>(true);

  useEffect(() => {
    const currentPath = location.pathname;

    // Only track if the path actually changed (or it's the initial load)
    if (prevLocationRef.current !== currentPath || isInitialLoadRef.current) {
      // Extract page name from path
      const pageName =
        currentPath === "/"
          ? "home"
          : currentPath.slice(1).split("/")[0] || "unknown";

      // Track page view
      analytics.trackPageView({
        page: currentPath,
        title: document.title,
        content_name: pageName,
        content_category: "page_view",
        isInitialLoad: isInitialLoadRef.current,
      });

      prevLocationRef.current = currentPath;
      isInitialLoadRef.current = false;
    }
  }, [location]);
}
